import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { Shirt, ExternalLink, Edit2, ShoppingBag, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

const SHOP_URL = 'https://soul2soledancewear.company.site/products/upa';

const DEFAULT_UNIFORM_DATA = {
  title: 'UPA Uniform Requirements',
  intro: 'All students should wear their UPA uniforms to every class. Looking smart and unified helps build team spirit and pride in our school!',
  requirements: [
    { item: 'Black leggings or trousers', required: true },
    { item: 'Black shoes (dance or comfortable trainers)', required: true },
    { item: 'UPA T-Shirt', required: true },
    { item: 'Hair tied back (for safety during movement)', required: true },
    { item: 'Water bottle', required: false },
  ],
  additionalInfo: 'For performances and shows, specific costume requirements will be communicated separately. Please ensure all items are clearly labeled with your child\'s name.',
  shopUrl: SHOP_URL
};

const Uniform = () => {
  const { user } = useAuth();
  const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff';
  
  const [uniformData, setUniformData] = useState(DEFAULT_UNIFORM_DATA);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(DEFAULT_UNIFORM_DATA);

  useEffect(() => {
    fetchUniformData();
  }, []);

  const fetchUniformData = async () => {
    try {
      const res = await api.get('/api/uniform-info');
      if (res.data) {
        setUniformData(res.data);
      }
    } catch (error) {
      console.log('Using default uniform data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.post('/api/uniform-info', editForm);
      setUniformData(editForm);
      setEditModalOpen(false);
      toast.success('Uniform information updated');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    }
  };

  const openEditModal = () => {
    setEditForm({ ...uniformData });
    setEditModalOpen(true);
  };

  // Official UPA uniform photos
  const products = [
    {
      name: 'UPA T-Shirt',
      image: 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/6c6ufw7q_8EB681B3-B805-4E72-AFE9-7B95C394CCA0.PNG',
      description: 'Official UPA branded t-shirt'
    },
    {
      name: 'UPA Top & Leggings',
      image: 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/pnut7n7b_4167092A-4473-4B26-AA6B-936A2D6A5B7D.jpg',
      description: 'Official UPA zip top and leggings'
    },
    {
      name: 'Black Trainers',
      image: 'https://static.prod-images.emergentagent.com/jobs/4cf29391-a054-46ca-8c58-6d2c15185384/images/99d641e31e6d61d977316808cb55435572a1eda02b8340a3b56b59da2e80fc5b.jpeg',
      description: 'Plain black trainers'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="uniform-page">
      <SEO page="uniform" />
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-bold text-white flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#FF007F] to-purple-600 rounded-xl flex items-center justify-center">
                  <Shirt className="w-7 h-7 text-white" />
                </div>
                {uniformData.title}
              </h1>
              <p className="text-white/60 mt-3 text-lg max-w-2xl">{uniformData.intro}</p>
            </div>
            <div className="flex gap-3">
              {isAdminOrStaff && (
                <Button 
                  onClick={openEditModal}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="edit-uniform-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button 
                onClick={() => window.open(uniformData.shopUrl || SHOP_URL, '_blank')}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold px-6"
                data-testid="shop-now-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Shop Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Requirements Section */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">What You Need</h2>
              <div className="space-y-3">
                {uniformData.requirements.map((req, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-3 p-4 rounded-lg ${req.required ? 'bg-[#FF007F]/10 border border-[#FF007F]/30' : 'bg-white/5 border border-white/10'}`}
                  >
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 ${req.required ? 'text-[#FF007F]' : 'text-white/40'}`} />
                    <span className="text-white">{req.item}</span>
                    {req.required && (
                      <span className="ml-auto text-xs text-[#FF007F] font-medium">Required</span>
                    )}
                  </div>
                ))}
              </div>

              {uniformData.additionalInfo && (
                <Card className="bg-white/5 border-white/10 mt-6">
                  <CardContent className="p-5">
                    <p className="text-white/70 text-sm">{uniformData.additionalInfo}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Shop Preview Section */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Shop UPA Uniform</h2>
              <div className="space-y-4">
                {products.map((product, index) => (
                  <Card 
                    key={index}
                    className="bg-white/5 border-white/10 hover:border-[#FF007F]/50 transition-all cursor-pointer overflow-hidden"
                    onClick={() => window.open(SHOP_URL, '_blank')}
                  >
                    <CardContent className="p-0 flex">
                      <div className="w-32 h-40 flex-shrink-0">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4 flex-1 flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-semibold">{product.name}</h3>
                          <p className="text-white/50 text-sm">{product.description}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-white/40" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Big CTA */}
              <Card className="bg-gradient-to-r from-[#FF007F] to-purple-600 border-0 mt-6 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Get Kitted Out!</h3>
                  <p className="text-white/80 mb-4">Visit our official shop for all UPA uniform items</p>
                  <Button 
                    onClick={() => window.open(SHOP_URL, '_blank')}
                    className="bg-white text-[#FF007F] hover:bg-white/90 font-bold px-8"
                    size="lg"
                    data-testid="big-shop-btn"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Visit Shop
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shirt className="w-5 h-5 text-[#FF007F]" />
              Edit Uniform Information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-white/70 mb-1 block">Page Title</label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Introduction</label>
              <Textarea
                value={editForm.intro}
                onChange={(e) => setEditForm({ ...editForm, intro: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Requirements (one per line, add * for required)</label>
              <Textarea
                value={editForm.requirements.map(r => r.required ? `${r.item}*` : r.item).join('\n')}
                onChange={(e) => {
                  const lines = e.target.value.split('\n');
                  const reqs = lines.filter(l => l.trim()).map(line => ({
                    item: line.replace('*', '').trim(),
                    required: line.includes('*')
                  }));
                  setEditForm({ ...editForm, requirements: reqs });
                }}
                className="bg-white/5 border-white/10 text-white resize-none font-mono text-sm"
                rows={6}
                placeholder="Black leggings or trousers*&#10;UPA T-Shirt*&#10;Water bottle"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Additional Information</label>
              <Textarea
                value={editForm.additionalInfo}
                onChange={(e) => setEditForm({ ...editForm, additionalInfo: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Shop URL</label>
              <Input
                value={editForm.shopUrl}
                onChange={(e) => setEditForm({ ...editForm, shopUrl: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-[#FF007F] hover:bg-[#D6006A] text-white">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Uniform;
